import qs from 'qs';

// --- Resource Name Constants ---
// Define the base path segments for your API resources
const ResourceNames = {
  AUTH: 'auth',
  OWNERS: 'owners',
  TENANTS: 'tenants',
  MANAGERS: 'managers',
  PROPERTIES: 'properties',
  PLANS: 'plans',
  CLAIMS: 'claims',
  INVOICES: 'invoices',
  PERMISSIONS: 'permissions',
  UPLOADS: 'uploads',
  ADMIN: 'admin',
  DOCUMENTS: 'documents',
  RATES: 'rates',
  PAYMENTS: 'payments',
  ADJUSTMENTS: 'adjustments',
  ME: 'me',
  PLAID: 'plaid',
  // Webhooks can be grouped or specific
  WEBHOOKS_ATLAS: 'atlas',
  WEBHOOKS_PLAID: 'plaid',
  WEBHOOKS_STRIPE: 'stripe',
};
Object.freeze(ResourceNames);

// --- URL Building Utilities ---
// Helper to build URL with query parameters, encoding correctly
const buildUrl = (base, params = {}) => {
  // Basic filtering for undefined/null, doesn't handle empty strings specifically here
  let cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      key !== 'page'
    ) {
      acc[key] = value;
    }
    return acc;
  }, {});

  if (params.page > 1) {
    cleanParams.offset = (params.page - 1) * params.limit;
  }

  console.log(params, cleanParams);

  // Use qs for robust query string generation
  const queryString = qs.stringify(cleanParams, {
    skipNulls: true, // Skip null/undefined values
    // arrayFormat: 'repeat', // Handle arrays correctly
    // encodeValuesOnly: true, // Avoid double-encoding issues
  });

  return queryString ? `${base}?${queryString}` : base;
};

// --- Endpoint Definitions (Using Handler IDs) ---
export const Endpoints = {
  // --- ADMIN ---
  getSummaryAsAdmin: () => `${ResourceNames.ADMIN}/summary`,
  getPropertyRatesInfoAsAdmin: () =>
    `${ResourceNames.ADMIN}/${ResourceNames.PROPERTIES}-${ResourceNames.RATES}-info`,

  // --- AUTH ---
  authLogin: () => `${ResourceNames.AUTH}/login`,
  emailLoginRequest: () => `${ResourceNames.AUTH}/email-login-request`,
  emailLoginExchange: () => `${ResourceNames.AUTH}/login-exchange`,

  // --- CLAIMS (Admin) ---
  getOneClaimAsAdmin: ({ claimID }) => `${ResourceNames.CLAIMS}/${claimID}`,
  updateClaimAsAdmin: ({ claimID }) => `${ResourceNames.CLAIMS}/${claimID}`,
  findClaimsAsAdmin: (params) => buildUrl(`${ResourceNames.CLAIMS}`, params),
  addClaimPaymentAsAdmin: ({ claimID }) =>
    `${ResourceNames.CLAIMS}/${claimID}/claim${ResourceNames.PAYMENTS}`,

  // --- INVOICES (Admin) ---
  findInvoiceAsAdmin: (params) => buildUrl(`${ResourceNames.INVOICES}`, params),
  getOneInvoiceAsAdmin: ({ invoiceID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}`,
  updateInvoiceAsAdmin: ({ invoiceID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}`,
  createAdjustmentAsAdmin: ({ invoiceID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.ADJUSTMENTS}`,
  updateAdjustmentAsAdmin: ({ invoiceID, adjustmentID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.ADJUSTMENTS}/${adjustmentID}`,
  deleteAdjustmentAsAdmin: ({ invoiceID, adjustmentID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.ADJUSTMENTS}/${adjustmentID}`,
  createPaymentAsAdmin: ({ invoiceID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.PAYMENTS}`,
  updatePaymentAsAdmin: ({ invoiceID, paymentID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.PAYMENTS}/${paymentID}`,
  deletePaymentAsAdmin: ({ invoiceID, paymentID }) =>
    `${ResourceNames.INVOICES}/${invoiceID}/${ResourceNames.PAYMENTS}/${paymentID}`,

  // --- MANAGERS (Admin) ---
  createManagerAsAdmin: () => `${ResourceNames.MANAGERS}`,
  findManagersAsAdmin: (params) =>
    buildUrl(`${ResourceNames.MANAGERS}`, params),
  getManagerAsAdmin: ({ managerID }) =>
    `${ResourceNames.MANAGERS}/${managerID}`,
  updateManagerAsAdmin: ({ managerID }) =>
    `${ResourceNames.MANAGERS}/${managerID}`,
  deleteManagerAsAdmin: ({ managerID }) =>
    `${ResourceNames.MANAGERS}/${managerID}`,
  loginManagerTokenAsAdmin: ({ managerID }) =>
    `${ResourceNames.MANAGERS}/${managerID}/login-token`,

  // --- MANAGER (Self) ---
  getMeAsManager: () => `${ResourceNames.MANAGERS}/${ResourceNames.ME}`,
  getPermissionsForManagerMe: () =>
    `${ResourceNames.MANAGERS}/${ResourceNames.ME}/${ResourceNames.PERMISSIONS}`,

  // --- OWNERS (Admin) ---
  getAllOwners: (params) => buildUrl(`${ResourceNames.OWNERS}`, params), // Admin getting list
  createOwnerAsAdmin: () => `${ResourceNames.OWNERS}`,
  updateOwnerAsAdmin: ({ ownerID }) => `${ResourceNames.OWNERS}/${ownerID}`,
  deleteOwnerAsAdmin: ({ ownerID }) => `${ResourceNames.OWNERS}/${ownerID}`,
  claimOwnerAsAdmin: ({ ownerID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/claim-account-request`,
  loginOwnerTokenAsAdmin: ({ ownerID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/login-token`,

  // --- OWNER (Self/Contextual - /owners/me) ---
  getOwnerMe: () => `${ResourceNames.OWNERS}/${ResourceNames.ME}`,
  updateOwnerAsOwner: () => `${ResourceNames.OWNERS}/${ResourceNames.ME}`,
  addNewEmailAsOwner: () =>
    `${ResourceNames.OWNERS}/${ResourceNames.ME}/add-new-email`,
  resendEmailLinkAsOwner: () =>
    `${ResourceNames.OWNERS}/${ResourceNames.ME}/resend-email-link`,
  deleteEmailAsOwner: () =>
    `${ResourceNames.OWNERS}/${ResourceNames.ME}/delete-email`,

  // --- OWNER (Contextual - /owners/:ownerID/...) ---
  // These are actions performed *by* an Owner or Manager *on* a specific Owner's resources
  findOwnerManagers: ({ ownerID, ...params }) =>
    buildUrl(
      `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.MANAGERS}`,
      params
    ),
  findOwnerProperties: ({ ownerID, params }) =>
    buildUrl(
      `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}`,
      params
    ),
  findOwnerClaims: ({ ownerID, params }) =>
    buildUrl(
      `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.CLAIMS}`,
      params
    ),
  getOwnerClaim: ({ ownerID, claimID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.CLAIMS}/${claimID}`,
  findOwnerPlans: ({ ownerID, params }) =>
    buildUrl(
      `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PLANS}`,
      params
    ),
  getOwnerProperty: ({ ownerID, propertyID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}/${propertyID}`,
  createPermission: ({ ownerID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PERMISSIONS}`,
  updatePermission: ({ ownerID, permissionID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PERMISSIONS}/${permissionID}`,
  getOnePermission: ({ ownerID, permissionID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PERMISSIONS}/${permissionID}`,
  createPlanWithTenant: ({ ownerID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PLANS}`,
  getOwnerPlan: ({ ownerID, planID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PLANS}/${planID}`,
  createDocumentOnProperty: ({ ownerID, propertyID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}/${propertyID}/${ResourceNames.DOCUMENTS}`,
  deleteDocumentFromProperty: ({ ownerID, propertyID, documentID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}/${propertyID}/${ResourceNames.DOCUMENTS}/${documentID}`,
  getOwnerManager: ({ ownerID, managerID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.MANAGERS}/${managerID}`,
  findOwnerInvoices: ({ ownerID, params }) =>
    buildUrl(
      `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.INVOICES}`,
      params
    ),
  getOwnerInvoice: ({ ownerID, invoiceID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.INVOICES}/${invoiceID}`,
  getOneOwner: ({ ownerID }) => `${ResourceNames.OWNERS}/${ownerID}`,
  updatePropertyRatesStatusAsOwner: ({ ownerID, propertyID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}/${propertyID}/${ResourceNames.RATES}`,
  updatePropertyManager: ({ ownerID, propertyID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}/${propertyID}/assign-manager`,
  acceptPermissionInvite: ({ ownerID, permissionID }) =>
    `${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PERMISSIONS}/${permissionID}/accept-invite`,

  // --- OWNER (Guest/Unauthenticated) ---
  confirmOwnerAccount: () => `${ResourceNames.OWNERS}/confirm-account`,

  // --- PLANS (Admin) ---
  createPlanAsAdmin: () => `${ResourceNames.PLANS}`,
  getOnePlanAsAdmin: ({ planID }) => `${ResourceNames.PLANS}/${planID}`,
  updatePlanAsAdmin: ({ planID }) => `${ResourceNames.PLANS}/${planID}`,
  findPlansAsAdmin: (params) => buildUrl(`${ResourceNames.PLANS}`, params),

  // --- PROPERTIES (Admin) ---
  createPropertyAsAdmin: () => `${ResourceNames.PROPERTIES}`,
  updatePropertyAsAdmin: ({ propertyID }) =>
    `${ResourceNames.PROPERTIES}/${propertyID}`,
  getAllPropertyAsAdmin: (params) =>
    buildUrl(`${ResourceNames.PROPERTIES}`, params),
  getPropertyAsAdmin: ({ propertyID }) =>
    `${ResourceNames.PROPERTIES}/${propertyID}`,
  deletePropertyAsAdmin: ({ propertyID }) =>
    `${ResourceNames.PROPERTIES}/${propertyID}`,
  createPropertyRatesAsAdmin: ({ propertyID }) =>
    `${ResourceNames.PROPERTIES}/${propertyID}/${ResourceNames.RATES}`,

  // --- TENANTS (Guest) ---
  registerAsTenant: () => `${ResourceNames.TENANTS}/register`,
  registerTenantWithProperty: () =>
    `${ResourceNames.TENANTS}/register-with-property`,
  resetTenantPasswordRequest: () =>
    `${ResourceNames.TENANTS}/reset-password-request`,
  resetTenantPassword: ({ resetPasswordJWT }) =>
    `${ResourceNames.TENANTS}/reset-password/${resetPasswordJWT}`,
  confirmTenantAccount: () => `${ResourceNames.TENANTS}/confirm-account`,
  loginTokenAsTenant: () => `${ResourceNames.TENANTS}/impersonate-token`,

  // --- TENANT (Self - /tenants/me) ---
  getMeTenant: () => `${ResourceNames.TENANTS}/${ResourceNames.ME}`,
  createDocumentAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.DOCUMENTS}`,
  deleteDocumentAsTenant: ({ documentID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.DOCUMENTS}/${documentID}`,
  resendTenantConfirmEmail: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/resend-confirm-email`,
  confirmTenantEmail: ({ confirmEmailJWT }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/confirm-email/${confirmEmailJWT}`,
  createPlanAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}`,
  updatePlanAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}`,
  submitPlanAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/submit`,
  getPlansAsTenant: (params) =>
    buildUrl(
      `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}`,
      params
    ),
  getPlanAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}`,
  getCoverageComparisonAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/coverage-comparison`,
  getCoverageComparisonAsAdmin: ({ planID }) =>
    `${ResourceNames.PLANS}/${planID}/coverage-comparison`,
  getPlanPdfAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/pdf`,
  deletePlanAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}`,
  createDocumentOnPlanAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/${ResourceNames.DOCUMENTS}`,
  deleteDocumentOnPlanAsTenant: ({ planID, documentID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/${ResourceNames.DOCUMENTS}/${documentID}`,
  createClaimAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}`,
  updateClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}`,
  deleteClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}`,
  getClaimsAsTenant: (params) =>
    buildUrl(
      `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}`,
      params
    ),
  getClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}`,
  finalizeClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}/finalize`,
  submitClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}/submit`,
  paymentClaimAsTenant: ({ claimID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.CLAIMS}/${claimID}/payment`,
  verifyPlaidIDAsTenant: ({ linkSessionID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLAID}/link/${linkSessionID}`,
  createPlaidVerificationAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLAID}/link`,
  createVerificationAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/create-verification-session`,
  requestSetupIntentAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/request-setup-intent`,
  addSetupIntentAsTenant: ({ setupIntentID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/add-setup-intent/${setupIntentID}`,
  paymentAsTenant: ({ planID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PLANS}/${planID}/payment`,
  getPaymentMethodsAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/payment-methods`,
  deletePaymentMethodAsTenant: ({ paymentMethodID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/payment-methods/${paymentMethodID}`,
  setDefaultPaymentMethodAsTenant: ({ paymentMethodID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/payment-methods/${paymentMethodID}/set-default`,
  updatePasswordAsTenant: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/update-password`,
  getTenantMessages: () =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/messages`,
  findAsTenantProperties: (params) =>
    buildUrl(
      `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PROPERTIES}`,
      params
    ),
  getPropertyAsTenant: ({ propertyID }) =>
    `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PROPERTIES}/${propertyID}`,
  getPropertyAsGuest: ({ propertyID }) =>
    `${ResourceNames.TENANTS}/guest/${ResourceNames.PROPERTIES}/${propertyID}`,
  getDistinctPropertyAddresses: (params) =>
    buildUrl(
      `${ResourceNames.TENANTS}/${ResourceNames.ME}/${ResourceNames.PROPERTIES}/addresses`,
      params
    ),
  getOwnerDistinctPropertyAddressesAsGuest: ({ ownerID }) =>
    `${ResourceNames.TENANTS}/guest/${ResourceNames.PROPERTIES}/${ownerID}/addresses`,
  getOwnerAsGuest: ({ ownerID }) =>
    `${ResourceNames.TENANTS}/guest/${ResourceNames.OWNERS}/${ownerID}`,
  findOwnerPropertiesAsGuest: ({ ownerID }) =>
    `${ResourceNames.TENANTS}/guest/${ResourceNames.OWNERS}/${ownerID}/${ResourceNames.PROPERTIES}`,  

  // --- TENANTS (Admin) ---
  findTenantsAsAdmin: (params) => buildUrl(`${ResourceNames.TENANTS}`, params),
  claimTenantAsAdmin: ({ tenantID }) =>
    `${ResourceNames.TENANTS}/${tenantID}/claim-account-request`,
  createTenantAsAdmin: () => `${ResourceNames.TENANTS}`,
  getTenantAsAdmin: ({ tenantID }) => `${ResourceNames.TENANTS}/${tenantID}`,
  deleteTenantAsAdmin: ({ tenantID }) => `${ResourceNames.TENANTS}/${tenantID}`,
  updateTenantAsAdmin: ({ tenantID }) => `${ResourceNames.TENANTS}/${tenantID}`,
  loginTokenAsAdmin: ({ tenantID }) =>
    `${ResourceNames.TENANTS}/${tenantID}/login-token`,

  // --- UPLOADS ---
  requestUploadUrlTenant: () => `${ResourceNames.UPLOADS}/request-url`,

  // --- WEBHOOKS ---
  stripeWebhook: () => `${ResourceNames.WEBHOOKS_STRIPE}/webhook/identity`,
  plaidWebhook: () => `${ResourceNames.WEBHOOKS_PLAID}/webhook`,
  atlasWebhook: () => `${ResourceNames.WEBHOOKS_ATLAS}/webhook`,
  atlasCron: () => `${ResourceNames.WEBHOOKS_ATLAS}/cron`,
};
